/**
 * Tar pack/unpack primitives.
 *
 * Pure of the network and of sessions — just `tar` over a project directory via
 * the child-process spawner. Reused by the `pull`/`push` composites. The exclude
 * set matches the bundles produced upstream (the VTM): build artifacts and local
 * state are dropped; `pnpm-lock.yaml` is kept (preflight needs it).
 *
 * @module
 */
import { Data, Effect, Stream } from "effect"
import { ChildProcess, ChildProcessSpawner } from "effect/unstable/process"

export class ArchiveError extends Data.TaggedError("ArchiveError")<{
  readonly message: string
}> {}

export const ARCHIVE_EXCLUDES = [
  "node_modules",
  "dist",
  ".astro",
  ".wrangler",
  ".git",
  ".env",
  ".heysegment",
]

const runTar = (
  args: ReadonlyArray<string>,
  label: string,
): Effect.Effect<void, ArchiveError, ChildProcessSpawner.ChildProcessSpawner> =>
  Effect.scoped(
    Effect.gen(function* () {
      const spawner = yield* ChildProcessSpawner.ChildProcessSpawner
      const command = ChildProcess.make("tar", args, { extendEnv: true })

      const handle = yield* spawner
        .spawn(command)
        .pipe(
          Effect.mapError(
            (e) => new ArchiveError({ message: `Could not start tar: ${e.message}` }),
          ),
        )

      const output = yield* Stream.mkString(handle.all.pipe(Stream.decodeText())).pipe(
        Effect.mapError(
          (e) => new ArchiveError({ message: `Could not read tar output: ${e.message}` }),
        ),
      )

      const exitCode = yield* handle.exitCode.pipe(
        Effect.mapError(
          (e) => new ArchiveError({ message: `tar did not complete: ${e.message}` }),
        ),
      )

      if (exitCode !== 0) {
        return yield* Effect.fail(
          new ArchiveError({
            message: `${label} failed (tar exit ${exitCode}): ${output.trim().slice(-400)}`,
          }),
        )
      }
    }),
  )

/** `tar czf <out> --exclude=… -C <projectDir> .` */
export const pack = (input: {
  readonly projectDir: string
  readonly out: string
}): Effect.Effect<void, ArchiveError, ChildProcessSpawner.ChildProcessSpawner> =>
  runTar(
    [
      "czf",
      input.out,
      ...ARCHIVE_EXCLUDES.map((entry) => `--exclude=${entry}`),
      "-C",
      input.projectDir,
      ".",
    ],
    "pack",
  )

/** `tar xzf <archive> -C <projectDir>` */
export const unpack = (input: {
  readonly archive: string
  readonly projectDir: string
}): Effect.Effect<void, ArchiveError, ChildProcessSpawner.ChildProcessSpawner> =>
  runTar(["xzf", input.archive, "-C", input.projectDir], "unpack")
